// src/components/dashboard/GaugePropertiesModal.tsx

import { useEffect, useState } from "react";
import { Modal, Button, Form, OverlayTrigger, Tooltip, Row, Col } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import http from "services/httpClient";

interface Label {
  label: string;
  column_index: number;
  is_enabled: boolean;
}

interface StatDefinition {
  id: number;
  source_column_index: number;
  aggregation: "min" | "max" | "avg";
  window_hours: 1 | 6 | 8 | 12 | 24;
  result_label: string;
  unit?: string | null;
}

interface GaugePropertiesModalProps {
  show: boolean;
  onClose: () => void;
  config: any;
  deviceId: number;
  refetchGraphs?: () => void;
}

export function GaugePropertiesModal({
  show,
  onClose,
  config,
  deviceId,
  refetchGraphs,
}: GaugePropertiesModalProps) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [selected, setSelected] = useState<number | "">("");

  const [formData, setFormData] = useState({
    title: config.title || "",
    unit: config.unit || "",
    min: config.min ?? 0,
    max: config.max ?? 100,

    low_operator: config.low_operator ?? "",
    low_threshold:
      config.low_threshold !== undefined && config.low_threshold !== null
        ? config.low_threshold
        : "",

    high_operator: config.high_operator ?? "",
    high_threshold:
      config.high_threshold !== undefined && config.high_threshold !== null
        ? config.high_threshold
        : "",
  });

  function InfoTooltip({ message }: { message: string }) {
    return (
      <OverlayTrigger placement="right" overlay={<Tooltip>{message}</Tooltip>}>
        <InfoCircleFill style={{ cursor: "pointer", marginLeft: "4px" }} size={14} />
      </OverlayTrigger>
    );
  }

  useEffect(() => {
    const fetchLabels = async () => {
      const [labelsRes, calcRes, statRes] = await Promise.all([
        http.get(`/device/${deviceId}/labels`),
        http.get(`/calculated_params/${deviceId}`),
        http.get(`/stat-definitions/device/${deviceId}`),
      ]);

      const rawLabels = labelsRes.data as Label[];

      const calcLabels = calcRes.data.map((c: any) => ({
        label: c.result_label,
        column_index: c.column_index,
        is_enabled: true,
      }));

      const statLabels = (statRes.data as StatDefinition[]).map((s) => ({
        label: s.result_label,
        column_index: 1000 + s.id,
        is_enabled: true,
      }));

      setLabels([...rawLabels, ...calcLabels, ...statLabels]);
    };

    if (show) {
      fetchLabels();

      const existing = config.device_label?.column_index ?? config.label_column_index;
      setSelected(existing ?? "");

      setFormData({
        title: config.title || "",
        unit: config.unit || "",
        min: config.min ?? 0,
        max: config.max ?? 100,

        low_operator: config.low_operator ?? "",
        low_threshold:
          config.low_threshold !== undefined && config.low_threshold !== null
            ? config.low_threshold
            : "",

        high_operator: config.high_operator ?? "",
        high_threshold:
          config.high_threshold !== undefined && config.high_threshold !== null
            ? config.high_threshold
            : "",
      });
    }
  }, [deviceId, show, config]);

  const handleSave = async () => {
    const min = Number(formData.min);
    const max = Number(formData.max);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      alert("Minimum and maximum values must be valid numbers.");
      return;
    }

    if (max <= min) {
      alert("Maximum value must be greater than minimum value.");
      return;
    }

    const lowThreshold =
      formData.low_threshold === "" ? null : Number(formData.low_threshold);
    const highThreshold =
      formData.high_threshold === "" ? null : Number(formData.high_threshold);

    if (lowThreshold !== null && !Number.isFinite(lowThreshold)) {
      alert("Low threshold must be a valid number.");
      return;
    }

    if (highThreshold !== null && !Number.isFinite(highThreshold)) {
      alert("High threshold must be a valid number.");
      return;
    }

    if (formData.low_operator && lowThreshold === null) {
      alert("Please enter a low threshold value.");
      return;
    }

    if (formData.high_operator && highThreshold === null) {
      alert("Please enter a high threshold value.");
      return;
    }

    if (lowThreshold !== null && !formData.low_operator) {
      alert("Please select a low operator.");
      return;
    }

    if (highThreshold !== null && !formData.high_operator) {
      alert("Please select a high operator.");
      return;
    }

    if (
      lowThreshold !== null &&
      highThreshold !== null &&
      highThreshold <= lowThreshold
    ) {
      alert("High threshold must be greater than low threshold.");
      return;
    }

    const updatedConfig = {
      ...config,
      label_column_index: selected === "" ? null : selected,
      min,
      max,

      low_operator: formData.low_operator || null,
      low_threshold: lowThreshold,

      high_operator: formData.high_operator || null,
      high_threshold: highThreshold,
    };

    if (!updatedConfig.low_operator || updatedConfig.low_threshold === null) {
      updatedConfig.low_operator = null;
      updatedConfig.low_threshold = null;
    }

    if (!updatedConfig.high_operator || updatedConfig.high_threshold === null) {
      updatedConfig.high_operator = null;
      updatedConfig.high_threshold = null;
    }

    await http.patch(`/graphs/${config.id}`, {
      graph_config: updatedConfig,
    });

    onClose();
    refetchGraphs?.();
  };

  return (
    <Modal show={show} onHide={onClose} size="sm" centered>
      <Modal.Header closeButton>
        <Modal.Title>Gauge Display Properties</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Select Label</Form.Label>
          <Form.Select
            value={selected}
            onChange={(e) =>
              setSelected(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            <option value="">-- Select Label --</option>
            {labels.map((l) => (
              <option
                key={l.column_index}
                value={l.column_index}
                disabled={!l.is_enabled}
              >
                {l.label} [x{l.column_index}]
                {!l.is_enabled ? " [Disabled]" : ""}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Minimum Value</Form.Label>
          <Form.Control
            type="number"
            value={formData.min}
            onChange={(e) =>
              setFormData({ ...formData, min: e.target.value === "" ? "" : Number(e.target.value) })
            }
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Maximum Value
            <InfoTooltip message="Max Value is for display scaling only. Alarm zones below are for left and right red sections of the gauge." />
          </Form.Label>
          <Form.Control
            type="number"
            value={formData.max}
            onChange={(e) =>
              setFormData({ ...formData, max: e.target.value === "" ? "" : Number(e.target.value) })
            }
          />
        </Form.Group>

        <hr />

        <Form.Label className="fw-bold">
          Low Display Zone
          <InfoTooltip message="DONT SET FOR SINGLE TRIG/ALARM! These low/high settings affect only the gauge display colors.
          Notification and alarm logic must still be configured in Trigger settings." />
        </Form.Label>
        <Row className="mb-3">
          <Col xs={5}>
            <Form.Select
              value={formData.low_operator}
              onChange={(e) =>
                setFormData({ ...formData, low_operator: e.target.value })
              }
            >
              <option value="">Off</option>
              <option value="<">{"<"}</option>
              <option value="<=">{"<="}</option>
            </Form.Select>
          </Col>
          <Col xs={7}>
            <Form.Control
              type="number"
              placeholder="Low threshold"
              value={formData.low_threshold}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  low_threshold: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
            />
          </Col>
        </Row>

        <Form.Label className="fw-bold">
          High Display Zone
          <InfoTooltip message="DONT SET FOR SINGLE TRIG/ALARM! These low/high settings affect only the gauge display colors.
          Notification and alarm logic must still be configured in Trigger settings." />
        </Form.Label>
        <Row className="mb-3">
          <Col xs={5}>
            <Form.Select
              value={formData.high_operator}
              onChange={(e) =>
                setFormData({ ...formData, high_operator: e.target.value })
              }
            >
              <option value="">Off</option>
              <option value=">">{">"}</option>
              <option value=">=">{">="}</option>
            </Form.Select>
          </Col>
          <Col xs={7}>
            <Form.Control
              type="number"
              placeholder="High threshold"
              value={formData.high_threshold}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  high_threshold: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
            />
          </Col>
        </Row>

        <div style={{ fontSize: "0.85rem", color: "#666" }}>
          Use low alarm for the left red zone and high alarm for the right red zone.
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default GaugePropertiesModal;